"use client"

import { apiFetch } from "./client"
import { accessTokenStore, registrationTokenStore } from "./token-store"
import type {
  AcceptInvitationResponse,
  OtpAck,
  OtpChannel,
  TokenResponse,
  UserEnvelope,
  UserResource,
  VerifyOtpResponse,
} from "./types"

/** Begin registration: send a one-time code to the chosen channel. */
export function registerStart(
  channel: OtpChannel,
  identifier: string
): Promise<OtpAck> {
  return apiFetch("/auth/register/start", {
    method: "POST",
    auth: "none",
    body: { channel, identifier },
  })
}

/** Re-issue the verification code for an in-progress registration. */
export function resendOtp(
  channel: OtpChannel,
  identifier: string
): Promise<OtpAck> {
  return apiFetch("/auth/register/resend-otp", {
    method: "POST",
    auth: "none",
    body: { channel, identifier },
  })
}

/** Verify the OTP and stash the short-lived registration token in memory. */
export async function verifyOtp(
  channel: OtpChannel,
  identifier: string,
  code: string
): Promise<VerifyOtpResponse> {
  const res = await apiFetch<VerifyOtpResponse>("/auth/register/verify-otp", {
    method: "POST",
    auth: "none",
    body: { channel, identifier, code },
  })
  registrationTokenStore.set(res.registration_token)
  return res
}

/**
 * Accept an admin-created customer invitation (scenario B). Skips OTP: the link
 * token proves ownership, so the backend marks the channel verified and issues
 * the short-lived registration token, which we stash for the set-password step.
 */
export async function acceptInvitation(
  token: string
): Promise<AcceptInvitationResponse> {
  const res = await apiFetch<AcceptInvitationResponse>(
    "/auth/register/accept-invitation",
    { method: "POST", auth: "none", body: { token } }
  )
  registrationTokenStore.set(res.registration_token)
  return res
}

/**
 * Accept a membership-queue invitation (scenario A). Same OTP-less exchange as
 * acceptInvitation, keyed by the queue id carried in the invite link.
 */
export async function acceptMembershipInvite(
  queue: string
): Promise<AcceptInvitationResponse> {
  const res = await apiFetch<AcceptInvitationResponse>(
    "/auth/register/accept-membership-invite",
    { method: "POST", auth: "none", body: { queue } }
  )
  registrationTokenStore.set(res.registration_token)
  return res
}

/** Complete registration: set password (uses registration token) and store the full access token. */
export async function setPassword(
  password: string,
  passwordConfirmation: string
): Promise<TokenResponse> {
  const res = await apiFetch<TokenResponse>("/auth/register/set-password", {
    method: "POST",
    auth: "registration",
    body: { password, password_confirmation: passwordConfirmation },
  })
  accessTokenStore.set(res.token)
  registrationTokenStore.clear()
  return res
}

/** Begin password reset: send a one-time code to the registered email/phone. */
export function forgotPassword(
  channel: OtpChannel,
  identifier: string
): Promise<OtpAck> {
  return apiFetch("/auth/password/forgot", {
    method: "POST",
    auth: "none",
    body: { channel, identifier },
  })
}

/** Verify the reset code and stash the short-lived reset token in memory. */
export async function verifyResetOtp(
  channel: OtpChannel,
  identifier: string,
  code: string
): Promise<void> {
  const res = await apiFetch<{ reset_token: string }>(
    "/auth/password/verify-otp",
    {
      method: "POST",
      auth: "none",
      body: { channel, identifier, code },
    }
  )
  registrationTokenStore.set(res.reset_token)
}

/** Complete password reset: set the new password (uses the reset token) and store the access token. */
export async function resetPassword(
  password: string,
  passwordConfirmation: string
): Promise<TokenResponse> {
  const res = await apiFetch<TokenResponse>("/auth/password/reset", {
    method: "POST",
    auth: "registration",
    body: { password, password_confirmation: passwordConfirmation },
  })
  accessTokenStore.set(res.token)
  registrationTokenStore.clear()
  return res
}

/** Email/phone + password login. Stores the access token on success. */
export async function login(
  channel: OtpChannel,
  identifier: string,
  password: string
): Promise<TokenResponse> {
  const res = await apiFetch<TokenResponse>("/auth/login", {
    method: "POST",
    auth: "none",
    body: { channel, identifier, password },
  })
  accessTokenStore.set(res.token)
  return res
}

/** Google sign-in: exchange a Google ID token for our access token (login-or-register). */
export async function loginWithGoogle(idToken: string): Promise<TokenResponse> {
  const res = await apiFetch<TokenResponse>("/auth/login/google", {
    method: "POST",
    auth: "none",
    body: { id_token: idToken },
  })
  accessTokenStore.set(res.token)
  return res
}

/** Revoke the token server-side, then clear it locally regardless of the result. */
export async function logout(): Promise<void> {
  // No token → nothing to revoke; avoid a guaranteed-401 that would re-trigger logout.
  if (!accessTokenStore.get()) {
    registrationTokenStore.clear()
    return
  }
  try {
    await apiFetch("/auth/logout", { method: "POST" })
  } finally {
    accessTokenStore.clear()
    registrationTokenStore.clear()
  }
}

/** Change the authenticated customer's password. Returns void (204). */
export async function changePassword(
  currentPassword: string,
  password: string,
  passwordConfirmation: string
): Promise<void> {
  await apiFetch("/auth/change-password", {
    method: "POST",
    body: {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    },
  })
}

/** Fetch the currently authenticated customer. */
export async function me(): Promise<UserResource> {
  const res = await apiFetch<UserEnvelope>("/auth/me")
  return res.data
}
